/**
 * Recursively flattens an object into { path: value } pairs.
 * Arrays are treated as leaf values and are not expanded.
 *
 * @example
 * flattenObject({ roof_condition: { value: 4, score: 0.8 } })
 * // → { 'roof_condition.value': 4, 'roof_condition.score': 0.8 }
 */
export function flattenObject(obj: any, prefix = "", result: Record<string, any> = {}): Record<string, any> {
  if (obj === null || obj === undefined) return result;

  Object.keys(obj).forEach((key) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = obj[key];

    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      flattenObject(value, path, result);
    } else {
      result[path] = value;
    }
  });

  return result;
}

/**
 * Converts the last segment of a dot-notation path into a human-readable label.
 * Suitable for UI display where parent context is already visible.
 *
 * @example
 * pathToLabel('roof_condition.missing_material_percent') → 'Missing Material Percent'
 */
export function pathToLabel(path: string): string {
  return path
    .split(".")
    .pop()!
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Converts ALL segments of a dot-notation path into a human-readable label.
 * Suitable for export contexts where key uniqueness is required across all fields.
 * Prevents collisions when multiple nested fields share the same last segment name
 * (e.g. detected, score, percentage across different report sub-categories).
 *
 * @example
 * pathToExportLabel('roof_condition_report.Missing Material.detected') → 'Roof Condition Report Missing Material Detected'
 * pathToExportLabel('roof_elements.ac_unit.count')                     → 'Roof Elements Ac Unit Count'
 */
export function pathToExportLabel(path: string): string {
  return path
    .split(".")
    .map((segment) => segment.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(" ");
}

/**
 * Infers a basic formatter purely from the path name, without manual config.
 *
 * Conventions covered automatically:
 * - Path ending in 'percent' → appends '%'   (e.g. missing_material_percent)
 * - Field '.value' with existing sibling '.unit' → concatenates the unit
 *
 * @param path - Dot-notation path of the field
 * @param flat - Full flattened object (used to look up sibling .unit fields)
 * @returns formatter or null if no convention applies
 */
export function inferFormatter(path: string, flat: Record<string, any>): ((v: any) => string) | null {
  const key = path.split(".").pop()!;

  if (key.endsWith("percent") || key === "percent") {
    return (v) => (v != null ? `${v}%` : "-");
  }

  const unitPath = path.replace(/\.value$/, ".unit");
  if (path.endsWith(".value") && flat[unitPath] != null) {
    return (v) => (v != null ? `${v} ${flat[unitPath]}` : "-");
  }

  return null;
}
